import { useState, useEffect } from 'react';
import { getCallTypes, overrideCallType, getCallTypeParameters } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Phone, Voicemail, FileCheck, UserCheck, Calendar, 
  GraduationCap, Gift, ClipboardCheck, Clock, Users,
  MessageCircle, HelpCircle, Edit2, Info, CheckCircle,
  Sparkles, AlertCircle, Star, Handshake, DollarSign,
  Building, TrendingUp, UserPlus
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Call type icon mapping
const CALL_TYPE_ICONS = {
  job_outreach: Phone,
  voicemail: Voicemail,
  submission_call: FileCheck,
  pre_screening: UserCheck,
  spotlight_call: Star,
  interview_request: Calendar,
  interview_prep: GraduationCap,
  interview_debrief: MessageCircle,
  job_offer: Gift,
  counteroffer: Handshake,
  salary_negotiation: DollarSign,
  onboarding: ClipboardCheck,
  placement_checkin: TrendingUp,
  timesheet: Clock,
  referral_check: Users,
  client_introduction: Building,
  follow_up: UserPlus,
  other: HelpCircle,
};

// Call type color mapping
const CALL_TYPE_COLORS = {
  job_outreach: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  voicemail: 'bg-muted/500/20 text-muted-foreground border-slate-500/30',
  submission_call: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  pre_screening: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  spotlight_call: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  interview_request: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  interview_prep: 'bg-green-500/20 text-green-400 border-green-500/30',
  interview_debrief: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  job_offer: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  counteroffer: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  salary_negotiation: 'bg-lime-500/20 text-lime-400 border-lime-500/30',
  onboarding: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  placement_checkin: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  timesheet: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  referral_check: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  client_introduction: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  follow_up: 'bg-fuchsia-500/20 text-fuchsia-400 border-fuchsia-500/30',
  other: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

export function CallTypeBadge({ callType, size = 'default', showIcon = true, className }) {
  const Icon = CALL_TYPE_ICONS[callType] || HelpCircle;
  const colorClass = CALL_TYPE_COLORS[callType] || CALL_TYPE_COLORS.other;
  
  // Format display name
  const displayName = callType?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
  
  return (
    <Badge className={cn(
      "border font-medium",
      colorClass,
      size === 'large' && "text-sm px-3 py-1",
      className
    )}>
      {showIcon && <Icon className={cn("mr-1.5", size === 'large' ? "h-4 w-4" : "h-3 w-3")} />}
      {displayName}
    </Badge>
  );
}

export function CallTypeSelector({ 
  call, 
  onUpdate, 
  showConfidence = true,
  allowEdit = true 
}) {
  const [open, setOpen] = useState(false);
  const [callTypes, setCallTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(call?.call_type || 'other');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [typeInfo, setTypeInfo] = useState(null);
  const [showReprocessPrompt, setShowReprocessPrompt] = useState(false);
  
  const currentCallType = call?.call_type || call?.detected_call_type || 'other';
  const confidence = call?.call_type_confidence || 0;
  const reasoning = call?.call_type_reasoning || '';
  const wasOverridden = !!call?.call_type_override;
  
  useEffect(() => {
    // Load available call types
    getCallTypes('staffing')
      .then(res => setCallTypes(res.data.call_types || []))
      .catch(err => console.error('Failed to load call types:', err));
  }, []);
  
  useEffect(() => {
    // Load info for selected type
    if (selectedType && selectedType !== 'other') {
      getCallTypeParameters(selectedType, 'staffing')
        .then(res => setTypeInfo(res.data))
        .catch(err => console.error('Failed to load type info:', err));
    }
  }, [selectedType]);
  
  const handleOverride = async () => {
    if (!selectedType || selectedType === currentCallType) {
      toast.info('Please select a different call type');
      return;
    }
    
    setIsLoading(true);
    try {
      await overrideCallType(call.id, selectedType, reason);
      toast.success('Call type updated successfully');
      setOpen(false);
      if (onUpdate) onUpdate(selectedType);
      
      // Ask if user wants to reprocess with the new call type
      setShowReprocessPrompt(true);
    } catch (error) {
      console.error('Failed to override call type:', error);
      toast.error('Failed to update call type');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleReprocessConfirm = async () => {
    try {
      // Import dynamically to avoid circular dependencies
      const { queueCallsForReprocess, startReprocessing } = await import('@/lib/api');
      await queueCallsForReprocess('', { call_ids: [call.id], reprocess_transcript: false, reprocess_analysis: true }, 'user');
      await startReprocessing('', 'user');
      toast.success('Call queued for reprocessing with new call type');
    } catch (error) {
      console.error('Failed to queue for reprocess:', error);
      toast.error('Failed to queue for reprocessing');
    }
    setShowReprocessPrompt(false);
  };
  
  return (
    <div className="flex items-center gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2 cursor-help">
              <CallTypeBadge callType={currentCallType} size="large" />
              {wasOverridden && (
                <Badge variant="outline" className="text-xs text-amber-400 border-amber-500/30">
                  <Edit2 className="h-3 w-3 mr-1" />
                  Override
                </Badge>
              )}
              {showConfidence && confidence > 0 && !wasOverridden && (
                <Badge variant="outline" className={cn(
                  "text-xs",
                  confidence >= 0.8 ? "text-green-400 border-green-500/30" :
                  confidence >= 0.5 ? "text-amber-400 border-amber-500/30" :
                  "text-red-400 border-red-500/30"
                )}>
                  <Sparkles className="h-3 w-3 mr-1" />
                  {Math.round(confidence * 100)}% AI
                </Badge>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-sm bg-slate-800 border-slate-700">
            <div className="space-y-2">
              {wasOverridden ? (
                <>
                  <p className="text-sm font-medium text-amber-400">Manually Overridden</p>
                  <p className="text-xs text-muted-foreground">
                    Originally detected as: {call.call_type_override?.original_detected?.replace(/_/g, ' ')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    By: {call.call_type_override?.overridden_by_name}
                  </p>
                  {call.call_type_override?.reason && (
                    <p className="text-xs text-slate-300">
                      Reason: {call.call_type_override.reason}
                    </p>
                  )}
                </>
              ) : reasoning ? (
                <>
                  <p className="text-sm font-medium text-white">AI Detection Reasoning</p>
                  <p className="text-xs text-slate-300">{reasoning}</p>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">AI detected call type</p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {allowEdit && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-muted-foreground hover:text-white"
              data-testid="edit-call-type-btn"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Phone className="h-5 w-5 text-blue-400" />
                Change Call Type
              </DialogTitle>
              <DialogDescription>
                Override the AI-detected call type. This helps improve scoring accuracy.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Current Type */}
              <div className="p-3 rounded-lg bg-slate-700/50">
                <p className="text-xs text-muted-foreground mb-1">Current Call Type</p>
                <CallTypeBadge callType={currentCallType} size="large" />
                {confidence > 0 && !wasOverridden && (
                  <p className="text-xs text-muted-foreground mt-2">
                    AI Confidence: {Math.round(confidence * 100)}%
                  </p>
                )}
              </div>
              
              {/* Select New Type */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-white">New Call Type</label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="bg-slate-700/50 border-slate-600" data-testid="call-type-select">
                    <SelectValue placeholder="Select call type" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 max-h-[300px]">
                    {callTypes.map(type => {
                      const Icon = CALL_TYPE_ICONS[type.value] || HelpCircle;
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <span>{type.name}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Type Info */}
              {typeInfo && selectedType !== currentCallType && (
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-blue-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-400 font-medium">
                        {typeInfo.call_type_info?.name}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {typeInfo.call_type_info?.description}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Typical duration: {typeInfo.call_type_info?.typical_duration}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {typeInfo.applicable_parameters?.length || 0} scoring parameters will apply
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Reason */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-white">Reason (Optional)</label>
                <Textarea
                  placeholder="Why are you changing the call type?"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="bg-slate-700/50 border-slate-600 resize-none"
                  rows={2}
                />
              </div>
              
              {/* Warning if same type */}
              {selectedType === currentCallType && (
                <div className="flex items-center gap-2 text-amber-400 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <span>Select a different call type to override</span>
                </div>
              )}
            </div>
            
            <DialogFooter className="mt-4">
              <Button 
                variant="outline" 
                onClick={() => setOpen(false)}
                className="border-slate-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleOverride}
                disabled={isLoading || selectedType === currentCallType}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-call-type-btn"
              >
                {isLoading ? (
                  <>Updating...</>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Update Call Type
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Reprocess Prompt Dialog */}
      <Dialog open={showReprocessPrompt} onOpenChange={setShowReprocessPrompt}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Reprocess Call?</DialogTitle>
            <DialogDescription>
              The call type has been updated. Would you like to reprocess this call with the new call type to regenerate the analysis and feedback?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowReprocessPrompt(false)}
              className="border-slate-600"
            >
              No, Keep Current Analysis
            </Button>
            <Button
              onClick={handleReprocessConfirm}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Yes, Reprocess Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CallTypeSelector;
